import { PageEvents } from '@griply/types';
import { getTimeFormat, getWeekStartsOn } from '@griply/utils';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useSortedSmartLists } from 'features/smart-lists';
import { useSortedTags } from 'features/tags';
import { useTaskTodayControls } from 'features/tasks';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CalendarSlotContextMenu } from 'shared/components/connected/calendar-slot-context-menu';
import { FullCalendarDay } from 'shared/components/ui/full-calendar-day';
import { useOpenTaskDetail } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useCalendarEventControls } from 'shared/hooks/use-calendar-event-controls';
import { useCalendarTaskSync } from 'shared/hooks/use-calendar-task-sync';
import { useElectron } from 'shared/hooks/use-is-mac';
import { useTodayScrollTime } from 'shared/hooks/use-today-scroll-time';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';
import { Paths } from 'shared/routes/paths';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { ID } from 'shared/types/id';
import { Timestamp } from 'shared/types/timestamp';
import { replaceUrlParams } from 'shared/utils/replace-url-params';
import { getScheduleByDate } from 'shared/utils/schedules';

import { useDayEvents } from '../../hooks/use-day-events';
import { useSidePanelTasks } from '../../hooks/use-side-panel-tasks';
import { FilterButton } from './filter-button';
import * as Styled from './side-panel.style';

export const SidePanel: React.FC = () => {
  useTrackPageVisitEvent(PageEvents.TodayCalendar, undefined);
  const today = useToday();
  const user = useUser();
  const { t } = useTranslation();
  const { isMac } = useElectron();
  const calendarContainerRef = React.useRef<HTMLDivElement>(null);

  const allTasks = useSidePanelTasks();

  const { events } = useDayEvents(today, allTasks);
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const tags = useSortedTags();
  const smartLists = useSortedSmartLists();

  const navigate = useNavigate();
  const openTaskDetail = useOpenTaskDetail();
  const { onComplete } = useTaskTodayControls();
  const {
    onEventChange,
    onEventReceive,
    onSelect,
    onDateTime,
    slotContextMenu,
  } = useCalendarEventControls(lifeAreas, goals, allTasks, tags, smartLists);
  const { calendarSyncStates, onToggleCalendarVisibility } =
    useCalendarTaskSync(allTasks);

  const [referenceDate, setReferenceDate] = useState(today);
  const onToday = useCallback(() => setReferenceDate(new Date()), []);
  const { scrollTime } = useTodayScrollTime(true, onToday);

  const _openEventDetail = useCallback(
    (id: ID) => {
      const task = allTasks.find(({ id: taskId }) => taskId === id);
      if (task) {
        openTaskDetail(task);
        return;
      }

      const goal = goals.find(({ id: goalId }) => goalId === id);
      if (goal) {
        navigate(replaceUrlParams(Paths.GoalDetail, { id: goal.id }));
        return;
      }

      const lifeArea = lifeAreas.find(
        ({ id: lifeAreaId }) => lifeAreaId === id,
      );
      if (lifeArea) {
        navigate(replaceUrlParams(Paths.LifeAreaDetail, { id: lifeArea.id }));
        return;
      }
    },
    [allTasks, goals, lifeAreas, navigate, openTaskDetail],
  );

  const _onComplete = useCallback(
    (id: ID, date: Timestamp) => {
      const task = allTasks.find(({ id: taskId }) => taskId === id);
      if (!task) {
        return;
      }
      const daySchedule = getScheduleByDate(task.schedules, { date, today });

      onComplete(task, ScheduleEntryState.Complete, {
        referenceDate: date,
        toggleDayEntry: !!daySchedule?.frequency,
      });
    },
    [allTasks, onComplete, today],
  );

  return (
    <Styled.Container>
      <Styled.Header $isElectronMac={isMac}>
        <Styled.Title>{t('pages.today.title')}</Styled.Title>

        <Styled.HeaderActions>
          <FilterButton />
        </Styled.HeaderActions>
      </Styled.Header>

      <Styled.Content>
        <FullCalendarDay
          ref={calendarContainerRef}
          tasks={allTasks}
          goals={goals}
          lifeAreas={lifeAreas}
          tags={tags}
          smartLists={smartLists}
          onCompleteTask={_onComplete}
          onClickEvent={_openEventDetail}
          calendarSyncStates={calendarSyncStates}
          onToggleCalendarVisibility={onToggleCalendarVisibility}
          events={events}
          eventChange={onEventChange}
          eventReceive={onEventReceive}
          dateClick={onDateTime}
          select={onSelect}
          referenceDate={referenceDate}
          scrollTime={scrollTime}
          timeFormat={getTimeFormat(user)}
          weekStartsOn={getWeekStartsOn(user)}
          selectable
          editable
          droppable
        />

        {slotContextMenu.slot && (
          <CalendarSlotContextMenu {...slotContextMenu} />
        )}
      </Styled.Content>
    </Styled.Container>
  );
};
